import { Link } from 'react-router-dom';
import type { PublicSettings } from '@brandonstrong/types';
import { Mock, Photo } from '../../components/Mock';
import { trackEvent } from '../../lib/analytics';

/**
 * Below-the-fold home sections. Everything wrapped in <Mock> is sample content.
 * The About teaser and the From the crew quotes are parked on the
 * keep/about-brandon-and-testimonials branch until the family wants them back.
 * Shirts are sold elsewhere and are deliberately not mentioned here.
 */
export function HomeSections({ s }: { s: PublicSettings | undefined }) {
  return (
    <>
      {/* Weekend at a glance */}
      <section className="border-y border-white/10 bg-night-900">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <div className="mb-10 flex items-end justify-between">
            <h2 className="text-4xl sm:text-5xl">The weekend</h2>
            <Link to="/event" className="text-meta text-base hover:text-ink-50">
              Full schedule →
            </Link>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {[
              [
                'Saturday',
                'Check in from three, then doors at six: the celebration starts in the ballroom with the music up. Dinner is served from nine to eleven, the stories run after, and we wrap around midnight.',
                'Welcome night',
              ],
              [
                'Sunday',
                'Breakfast together, the group photo on the front steps, and check-out by noon. Nobody is in a hurry; the farewell is in the parking lot.',
                'Farewell',
              ],
            ].map(([day, body, label]) => (
              <Mock key={day}>
                <p className="text-meta text-lg">{day}</p>
                <h3 className="mt-2 text-3xl">{label}</h3>
                <p className="mt-3 text-ink-200">{body}</p>
              </Mock>
            ))}
          </div>
        </div>
      </section>

      {/* Dress code */}
      <section className="bg-stage bg-hex">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-20 lg:grid-cols-[1fr_1.2fr] lg:items-center">
          <div>
            <p className="label-highlight text-xl">Dress code</p>
            <h2 className="mt-4 text-5xl sm:text-6xl">Rockstar</h2>
          </div>
          <Mock>
            <p className="text-lg text-ink-100">
              Come dressed for the show, not the service. Leather and denim, band tees, boots,
              studs, sunglasses at night, big hair, glitter if you dare. Black is fine if it is a
              band shirt; leave the suits and the ties at home. Saturday night is the main event,
              so save the loudest outfit for six o&apos;clock, and tag your photos #BrandonStrong.
            </p>
          </Mock>
        </div>
      </section>

      {/* Give */}
      <section className="mx-auto grid max-w-6xl gap-10 px-4 py-20 lg:grid-cols-[1.2fr_1fr] lg:items-center">
        <div>
          <h2 className="text-4xl sm:text-5xl">Surrounding Brandon and Shawna with love</h2>
          <Mock className="mt-6">
            <p className="text-lg text-ink-200">
              Room reservations cover the weekend itself. Everything given through the family&apos;s
              GoFundMe goes to Shawna: it was set up for the medical expenses, medications, living
              expenses and transportation of Brandon&apos;s care, and it is the one place to give.
            </p>
            <p className="mt-4 text-lg text-ink-200">
              If you cannot make the weekend, this is the way to be in the room anyway.
            </p>
          </Mock>
          <div className="mt-8 flex flex-wrap gap-4">
            {s?.donationUrl ? (
              <a
                href={s.donationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-accent"
                onClick={() => trackEvent('donate_click')}
              >
                Donate to the family
              </a>
            ) : (
              <Link to="/donate" className="btn-accent">
                Donate to the family
              </Link>
            )}
            <Link to="/rooms" className="btn-outline">
              Reserve a room
            </Link>
          </div>
        </div>
        <Photo name="home-give.jpg" alt="Brandon and Shawna" className="aspect-[4/3] w-full" />
      </section>

      {/* Gallery strip: three photos, none of them used elsewhere on this page. */}
      <section className="grid grid-cols-3">
        {['home-1.jpg', 'home-2.jpg', 'home-3.jpg'].map((name, i) => (
          <Photo key={name} name={name} alt={`Photo ${i + 1}`} className="aspect-square" />
        ))}
      </section>
      <div className="mx-auto max-w-6xl px-4 py-6 text-right">
        <Link to="/photos" className="text-meta text-base hover:text-ink-50">
          All the photos &rarr;
        </Link>
      </div>
    </>
  );
}
